import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import useIsMounted from "../../../../hooks/use-is-mounted";
import ProductTagChip from "../../product-tag-chip";
import {getProductTagsKey, normalizeProductTags} from "../../product-tag-chip/normalize-product-tags";
import './index.scss';

/**
 * Adds the 'static-svg' class to the great-grandparent element of each svg with 'static-svg' class, and to the
 * @param container
 */
const injectStaticSvgClasses = (container) => {
    if (!container)
        return;
    const SVGs = container.querySelectorAll('svg');
    for (const svg of SVGs) {
        svg.parentElement?.parentElement?.parentElement?.parentElement?.classList.remove('static-svg');
        if (svg.classList.contains('static-svg')) {
            svg.parentElement?.parentElement?.parentElement?.parentElement?.classList.add('static-svg');
        }
    }
    const PNGs = container.querySelectorAll('img');
    for (const png of PNGs) {
        png.parentElement?.parentElement?.classList.remove('static-svg');
        png.parentElement?.parentElement?.classList.add('static-svg');
    }
};

const ProductTagsContainer = ({
                                  tags,
                                  onTagClicked: onTagClickedProps,
                              }) => {
    const [state, setState] = useState({tags: []});
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<HTMLDivElement | null>}*/
    const containerRef = useRef(null);

    const tagsKey = useMemo(() => getProductTagsKey(tags), [tags]);
    const normalizedTags = useMemo(() => normalizeProductTags(tags), [tagsKey]);

    const iconSize = useMemo(() => 20, []);

    /**
     * With each change in tagsKey or normalizedTags:
     */
    useLayoutEffect(() => {
        setState({tags: normalizedTags});
    }, [tagsKey, normalizedTags])

    /**
     * With each change in state.tags:
     */
    useEffect(() => {
        if (!containerRef.current)
            return;
        injectStaticSvgClasses(containerRef.current);
        const timeoutId = window.setTimeout(() => injectStaticSvgClasses(containerRef.current), 150);
        return () => window.clearTimeout(timeoutId);
    }, [state.tags]);

    /**
     * Adds the 'static-svg' class to the great-grandparent element of each svg with 'static-svg' class, and to the
     * @type {(function(): void)|*}
     */
    const onTagReady = useCallback(() => {
        if (!containerRef.current)
            return;
        injectStaticSvgClasses(containerRef.current);
    }, []);

    /**
     * Calls the onTagClickedProps callback with the given tag if the component is still mounted and the callback exists, and prevents the default behavior and propagation of the event.
     * @type {(function(*, *): void)|*}
     */
    const onTagClicked = useCallback((e, tag) => {
        if (!isMounted() || !onTagClickedProps)
            return;
        e.preventDefault();
        e.stopPropagation();
        onTagClickedProps(tag);
    }, [isMounted, onTagClickedProps]);

    if (!normalizedTags.length) {
        return null;
    }

    return (
        <div className={classnames('tags-container-wrapper')}>
            <div ref={containerRef} className={'tags-container'}>
                {
                    state?.tags?.map(tag => (
                        <ProductTagChip
                            key={tag?.id}
                            tag={tag}
                            iconSize={iconSize}
                            onTagReady={onTagReady}
                            onTagClicked={onTagClickedProps ? onTagClicked : undefined}
                        />
                    ))
                }
            </div>
        </div>
    );
}

export default ProductTagsContainer;
